// Desenho do grafo no canvas, portado de graph.js:337-437.
//
// A única mudança de fundo: as cores chegam por uma função `cor`, em vez de o
// desenho ler `getComputedStyle(document.documentElement)` a cada traço. O
// motivo é medido, não estético — o `cv()` antigo era chamado por aresta e por
// nó dentro de um laço a 60 quadros por segundo, e com 240 nós isso é milhares
// de leituras de estilo por segundo. Quem chama resolve as variáveis uma vez por
// tema (ver useGrafo).
import {
  ALTURA_DA_BANDA, BAND, BAND_NAME, KIND, REL_COLOR, casaComBusca, paraTela, rotulo,
  saoVizinhos, type Aresta, type Camera, type Modo, type No,
} from './nucleo';

export type Cor = (variavel: string) => string;

export interface Cena {
  ctx: CanvasRenderingContext2D;
  largura: number;
  altura: number;
  camera: Camera;
  nos: No[];
  arestas: Aresta[];
  mapa: Map<string, No>;
  rank: Map<number, number>;
  layout: 'layers' | 'force';
  modo: Modo;
  selecionado: No | null;
  sobre: No | null;
  foco: Set<string> | null;
  busca: string;
  rotulos: boolean;
  /** notas do time por arquivo, para o selo no canto do nó */
  notasPorArquivo: Record<string, number>;
  cor: Cor;
  graus: Map<string, number>;
}

interface Rotulo { n: No; p: { x: number; y: number }; r: number; pri: number; txt: string }

const estiloDaAresta = (c?: string | null) => {
  if (c === 'inferred') return { dash: [6, 4], a: 0.85 };
  if (c === 'ambiguous') return { dash: [2, 4], a: 0.7 };
  return { dash: [] as number[], a: 1 };
};

export function desenhar(c: Cena): void {
  const { ctx, largura: W, altura: H, camera, cor } = c;
  if (!W || !H) return; // painel escondido: nada a desenhar
  ctx.clearRect(0, 0, W, H);
  const foc = c.selecionado || c.sobre;
  const K = camera.k;

  if (c.layout === 'layers') desenharBandas(c);

  for (const e of c.arestas) {
    const a = c.mapa.get(e.src);
    const b = c.mapa.get(e.dst);
    if (!a || !b) continue;
    if (c.foco && !(c.foco.has(e.src) && c.foco.has(e.dst))) continue;
    // deriva é desenhada quebrada: a decisão cita código que não existe mais
    const s = e.broken ? { dash: [2, 5], a: 0.9 } : estiloDaAresta(e.confidence);
    const quente = !!foc && (foc.node_key === e.src || foc.node_key === e.dst);
    const A = paraTela(a, camera, W, H);
    const B = paraTela(b, camera, W, H);
    ctx.save();
    ctx.strokeStyle = cor(REL_COLOR[e.rel] || '--ink-faint');
    ctx.globalAlpha = (foc ? (quente ? 0.95 : 0.07) : 0.34) * s.a;
    ctx.lineWidth = quente ? 2.2 : 1.1;
    ctx.setLineDash(s.dash);
    ctx.beginPath();
    ctx.moveTo(A.x, A.y);
    ctx.lineTo(B.x, B.y);
    ctx.stroke();
    if ((quente || K > 0.75) && K >= 0.35) { // seta na ponta do destino
      const ang = Math.atan2(B.y - A.y, B.x - A.x);
      const off = (b.r * K) + 3;
      const px = B.x - Math.cos(ang) * off;
      const py = B.y - Math.sin(ang) * off;
      const h = quente ? 9 : 6;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(px - Math.cos(ang - 0.4) * h, py - Math.sin(ang - 0.4) * h);
      ctx.lineTo(px - Math.cos(ang + 0.4) * h, py - Math.sin(ang + 0.4) * h);
      ctx.closePath();
      ctx.fillStyle = ctx.strokeStyle;
      ctx.fill();
    }
    ctx.restore();
  }

  const rotulos: Rotulo[] = [];
  for (const n of c.nos) {
    if (c.foco && !c.foco.has(n.node_key)) continue;
    const P = paraTela(n, camera, W, H);
    const r = Math.max(3, n.r * K);
    if (P.x < -60 || P.y < -60 || P.x > W + 60 || P.y > H + 60) continue;
    // cobertura: verde quem tem memória, cinza apagado quem ninguém documentou
    const semMemoria = c.modo === 'cobertura' && !n.memory;
    const col = c.modo === 'cobertura'
      ? ((n.memory ?? 0) >= 3 ? cor('--ok') : n.memory === 2 ? cor('--brand')
        : n.memory === 1 ? cor('--brand-2') : cor('--ink-faint'))
      : cor(KIND[n.kind] || '--brand');
    const achado = casaComBusca(n, c.busca);
    const apagado = (!!foc && foc !== n && !saoVizinhos(c.arestas, foc, n)) || (!!c.busca && !achado);
    ctx.save();
    ctx.globalAlpha = apagado ? 0.22 : (semMemoria ? 0.34 : 1);
    const quente = n === foc;
    // não confirmado, em revisão ou não commitado: contorno tracejado, mesma
    // linguagem visual para "existe, mas ninguém do time chancelou ainda"
    const provisorio = n.wt || n.review_state === 'nao_confirmada' || n.review_state === 'revisar'
      || n.drift || semMemoria;
    ctx.beginPath();
    if (n.kind === 'endpoint' || n.kind === 'arquivo' || n.kind === 'unidade') {
      ctx.rect(P.x - r, P.y - r * 0.7, r * 2, r * 1.4);
    } else ctx.arc(P.x, P.y, r, 0, 7);
    if (quente) { ctx.shadowColor = col; ctx.shadowBlur = 16; }
    ctx.fillStyle = col;
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.lineWidth = quente ? 3 : provisorio ? 2.6 : 2.2;
    ctx.strokeStyle = quente ? cor('--ink')
      : n.review_state === 'contestada' ? cor('--bad')
        : provisorio && !semMemoria ? cor('--warn') : cor('--surface');
    if (provisorio) ctx.setLineDash([3, 2]);
    ctx.stroke();
    ctx.setLineDash([]);
    if (achado) {
      ctx.beginPath();
      ctx.arc(P.x, P.y, r + 5, 0, 7);
      ctx.strokeStyle = cor('--brand');
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    const notas = n.file ? (c.notasPorArquivo[n.file] || 0) : 0;
    if (notas && K > 0.5) {
      ctx.beginPath();
      ctx.arc(P.x + r * 0.85, P.y - r * 0.85, 6, 0, 7);
      ctx.fillStyle = cor('--brand-2');
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.font = '700 8px ui-monospace,monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(notas), P.x + r * 0.85, P.y - r * 0.85);
      ctx.textBaseline = 'alphabetic';
    }
    ctx.restore();

    const grande = ['file', 'controller', 'service', 'module', 'endpoint'].includes(n.kind);
    // quem colide cai fora em desenharRotulos, por prioridade
    if (c.rotulos && !apagado && (quente || achado || K > 0.5)) {
      rotulos.push({ n, p: P, r, pri: quente ? 3 : achado ? 2 : grande ? 1 : 0, txt: rotulo(n) });
    }
  }
  desenharRotulos(c, rotulos);
}

function desenharBandas(c: Cena): void {
  const { ctx, largura: W, altura: H, cor } = c;
  ctx.save();
  ctx.font = '700 10px ui-monospace,monospace';
  ctx.textAlign = 'left';
  for (const [banda, rank] of c.rank) {
    const y = paraTela({ x: 0, y: rank * ALTURA_DA_BANDA }, c.camera, W, H).y;
    if (y < -20 || y > H + 20) continue;
    ctx.strokeStyle = cor('--line');
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 6]);
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(W, y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = cor('--ink-faint');
    ctx.fillText((BAND_NAME[banda] || '').toUpperCase(), 10, y - 6);
  }
  ctx.restore();
}

/**
 * Rótulo que não cabe é rótulo que atrapalha: os mais relevantes desenham
 * primeiro e quem colidir com um já desenhado fica de fora (volta no zoom).
 */
function desenharRotulos(c: Cena, rotulos: Rotulo[]): void {
  const { ctx, cor } = c;
  ctx.save();
  ctx.font = `600 ${Math.min(12, 10 + c.camera.k).toFixed(0)}px ui-monospace,monospace`;
  ctx.textAlign = 'center';
  const caixas: { x0: number; x1: number; y0: number; y1: number }[] = [];
  rotulos.sort((a, b) => b.pri - a.pri
    || (c.graus.get(b.n.node_key) || 0) - (c.graus.get(a.n.node_key) || 0));
  for (const L of rotulos) {
    const w = ctx.measureText(L.txt).width;
    const h = 13;
    const caixa = { x0: L.p.x - w / 2 - 3, x1: L.p.x + w / 2 + 3, y0: L.p.y - L.r - 7 - h, y1: L.p.y - L.r - 4 };
    if (caixas.some((b) => caixa.x0 < b.x1 && caixa.x1 > b.x0 && caixa.y0 < b.y1 && caixa.y1 > b.y0)) continue;
    caixas.push(caixa);
    ctx.fillStyle = cor('--surface');
    ctx.globalAlpha = 0.75;
    ctx.fillRect(caixa.x0, caixa.y0 + 2, caixa.x1 - caixa.x0, h);
    ctx.globalAlpha = 1;
    ctx.fillStyle = cor('--ink');
    ctx.fillText(L.txt, L.p.x, L.p.y - L.r - 7);
  }
  ctx.restore();
}

/** Nome legível da banda, para o painel em texto e para o teste. */
export const nomeDaBanda = (kind: string): string => BAND_NAME[BAND[kind] ?? 5] || '';
